import { useParams, useNavigate } from 'react-router-dom';
import {
  useLeaveApplication,
  useSubmitLeave,
  useCancelLeave,
  useDeleteLeave,
} from '@saiens/shared/hooks';
import { getLeaveStateLabel, formatWorkHours } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  ArrowLeft,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Send,
  Undo2,
  Trash2,
} from 'lucide-react';
import type { LeaveState } from '@saiens/shared/types';

function StateBadge({ state }: { state: LeaveState }) {
  const config: Record<LeaveState, { bg: string; text: string; icon: typeof FileText }> = {
    draft: { bg: 'bg-gray-100', text: 'text-gray-600', icon: FileText },
    f_approve: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
    agree: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
    refused: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
  };
  const c = config[state] || config.draft;
  const Icon = c.icon;

  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${c.bg} ${c.text}`}>
      <Icon className="h-3.5 w-3.5" />
      {getLeaveStateLabel(state)}
    </span>
  );
}

export function LeaveDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const applicationId = id ? Number(id) : undefined;

  const { data: detail, isLoading } = useLeaveApplication(applicationId);
  const submitLeave = useSubmitLeave();
  const cancelLeave = useCancelLeave();
  const deleteLeave = useDeleteLeave();

  const handleSubmit = () => {
    if (!applicationId) return;
    submitLeave.mutate(applicationId);
  };

  const handleCancel = () => {
    if (!applicationId) return;
    cancelLeave.mutate(applicationId);
  };

  const handleDelete = () => {
    if (!applicationId) return;
    deleteLeave.mutate(applicationId, {
      onSuccess: () => {
        navigate('/leave', { replace: true });
      },
    });
  };

  if (isLoading || !detail) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const isActing = submitLeave.isPending || cancelLeave.isPending || deleteLeave.isPending;

  return (
    <div className="mx-auto max-w-lg">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center gap-3 bg-white px-4 py-3 shadow-sm">
        <button onClick={() => navigate(-1)} className="rounded-full p-1 hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-semibold">請假明細</h1>
      </div>

      <div className="px-4 py-4">
        {/* Status & Number */}
        <div className="mb-4 flex items-center justify-between">
          <span className="text-sm text-gray-500">{detail.name}</span>
          <StateBadge state={detail.state} />
        </div>

        {/* Info Card */}
        <div className="mb-4 space-y-3 rounded-xl bg-white p-4 shadow-sm">
          <InfoRow label="假別" value={detail.holiday_type_name} />
          <InfoRow
            label="日期"
            value={
              detail.start_day === detail.end_day
                ? detail.start_day || '-'
                : `${detail.start_day || ''} ~ ${detail.end_day || ''}`
            }
          />
          {detail.time_type !== 'day' && (
            <InfoRow
              label="時間"
              value={detail.display_time || '-'}
            />
          )}
          <InfoRow
            label="時數"
            value={formatWorkHours(detail.holiday_time_total)}
            highlight
          />
          <InfoRow label="代理人" value={detail.substitute_name || '-'} />
          <InfoRow label="剩餘額度" value={formatWorkHours(detail.last_time)} />
          {detail.report_note && (
            <InfoRow label="事由" value={detail.report_note} />
          )}
          {detail.holiday_note && (
            <InfoRow label="備註" value={detail.holiday_note} />
          )}
          <InfoRow label="申請人" value={detail.employee_name || '-'} />
          <InfoRow label="部門" value={detail.department_name || '-'} />
          <InfoRow label="申請時間" value={detail.create_date || '-'} />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pb-4">
          {detail.state === 'draft' && (
            <>
              <button
                onClick={handleDelete}
                disabled={isActing}
                className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-red-200 bg-white py-3 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-40"
              >
                <Trash2 className="h-4 w-4" />
                {deleteLeave.isPending ? '刪除中...' : '刪除'}
              </button>
              <button
                onClick={handleSubmit}
                disabled={isActing}
                className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-pink-500 py-3 text-sm font-medium text-white hover:bg-pink-600 disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
                {submitLeave.isPending ? '送出中...' : '送出申請'}
              </button>
            </>
          )}
          {detail.state === 'f_approve' && (
            <button
              onClick={handleCancel}
              disabled={isActing}
              className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-yellow-300 bg-white py-3 text-sm font-medium text-yellow-700 hover:bg-yellow-50 disabled:opacity-40"
            >
              <Undo2 className="h-4 w-4" />
              {cancelLeave.isPending ? '撤銷中...' : '撤銷申請'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoRow({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-start justify-between">
      <span className="text-sm text-gray-500">{label}</span>
      <span
        className={`text-right text-sm ${
          highlight ? 'font-semibold text-pink-600' : 'text-gray-900'
        }`}
      >
        {value}
      </span>
    </div>
  );
}
